import { useState, useRef, useEffect, useMemo } from 'react';
import { Sparkles, Send, X, Bot, Minus } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { buildAssistantContext } from '../../lib/assistantContext';

const SUGGESTIONS = [
  'How is LATAM tracking vs plan this year?',
  '¿Qué país está más abajo del AOP?',
  'Como está o sell-out no último trimestre?',
];

// Lightweight markdown rendering (bold, bullets, headers) so the model's
// **bold** / "- " output shows formatted instead of raw asterisks/dashes.
const renderInline = (text) =>
  text.split(/(\*\*.+?\*\*)/g).map((p, i) =>
    p.startsWith('**') && p.endsWith('**')
      ? <strong key={i}>{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>
  );

const splitCells = (line) => {
  let l = line.trim();
  if (l.startsWith('|')) l = l.slice(1);
  if (l.endsWith('|')) l = l.slice(0, -1);
  return l.split('|').map((c) => c.trim());
};
const isTableSep = (line) => /^[\s|:-]+$/.test(line) && line.includes('-') && line.includes('|');

const RichText = ({ text }) => {
  const lines = text.split('\n');
  const out = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    // Markdown table: header row + separator + body rows.
    if (line.includes('|') && i + 1 < lines.length && isTableSep(lines[i + 1]) && splitCells(line).length >= 2 && splitCells(lines[i + 1]).length === splitCells(line).length) {
      const header = splitCells(line);
      const rows = [];
      let j = i + 2;
      while (j < lines.length && lines[j].trim() && lines[j].includes('|')) { rows.push(splitCells(lines[j])); j++; }
      out.push(
        <div key={i} className="my-2 overflow-x-auto">
          <table className="text-xs border-collapse">
            <thead><tr>{header.map((h, k) => <th key={k} className="border border-slate-200 bg-slate-100 px-2 py-1 text-left font-semibold whitespace-nowrap">{renderInline(h)}</th>)}</tr></thead>
            <tbody>{rows.map((r, ri) => <tr key={ri} className={ri % 2 ? 'bg-slate-50' : ''}>{r.map((c, ci) => <td key={ci} className="border border-slate-200 px-2 py-1 whitespace-nowrap">{renderInline(c)}</td>)}</tr>)}</tbody>
          </table>
        </div>
      );
      i = j - 1;
      continue;
    }
    if (!line.trim()) { out.push(<div key={i} className="h-2" />); continue; }
    const hm = line.match(/^#{1,6}\s+(.*)$/);
    if (hm) { out.push(<div key={i} className="font-bold mt-1">{renderInline(hm[1])}</div>); continue; }
    const l = line.replace(/^(\s*)[-*]\s+/, '$1• ');
    out.push(<div key={i}>{renderInline(l)}</div>);
  }
  return <>{out}</>;
};

export const AssistantWidget = ({ forecastData, sellInData, sellOutData, inventoryData, digitalData, insightsData, projects, deals, businessCases }) => {
  const { session } = useAuth();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]); // {role, content}
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Rebuild context only when the underlying data changes.
  const context = useMemo(
    () => buildAssistantContext({ forecastData, sellInData, sellOutData, inventoryData, digitalData, insightsData, projects, deals, businessCases }),
    [forecastData, sellInData, sellOutData, inventoryData, digitalData, insightsData, projects, deals, businessCases],
  );

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, busy]);
  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 50); }, [open]);

  const ask = async (text) => {
    const question = (text ?? input).trim();
    if (!question || busy) return;
    setInput('');
    const history = messages.slice();
    setMessages((m) => [...m, { role: 'user', content: question }, { role: 'assistant', content: '' }]);
    setBusy(true);

    // Abort if the whole round-trip takes too long, so the UI never hangs on "busy".
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 130000);
    try {
      let token = session?.access_token;
      // Refresh if we somehow don't have it in memory.
      if (!token) { const { data: s } = await supabase.auth.getSession(); token = s?.session?.access_token; }

      const res = await fetch('/api/assistant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token || ''}` },
        body: JSON.stringify({ question, context, history }),
        signal: ctrl.signal,
      });

      if (!res.ok) {
        let msg = 'Sorry — I hit an error. Please try again.';
        if (res.status === 503) msg = 'The assistant is not configured yet (missing API key). Ask your admin to set it up in Vercel.';
        else if (res.status === 401) msg = 'Your session expired. Please refresh the page and sign in again.';
        else if (res.status === 429) msg = "You've reached the usage limit for now. Please try again in a little while.";
        setMessages((m) => { const c = m.slice(); c[c.length - 1] = { role: 'assistant', content: msg }; return c; });
        setBusy(false);
        return;
      }

      // Stream the plain-text answer.
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => { const c = m.slice(); c[c.length - 1] = { role: 'assistant', content: acc }; return c; });
      }
      if (!acc.trim()) {
        setMessages((m) => { const c = m.slice(); c[c.length - 1] = { role: 'assistant', content: 'I didn’t get a response. Please try again.' }; return c; });
      }
    } catch (e) {
      const msg = e && e.name === 'AbortError'
        ? 'The request took too long and timed out. Please try again.'
        : 'Network error — please try again.';
      setMessages((m) => { const c = m.slice(); c[c.length - 1] = { role: 'assistant', content: msg }; return c; });
    } finally {
      clearTimeout(timer);
      setBusy(false);
    }
  };

  const onKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); } };

  return (
    <>
      {/* Launcher */}
      {!open && (
        <button onClick={() => setOpen(true)} title="Ask the dashboard assistant"
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 pl-3.5 pr-4 py-3 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-900/30 hover:shadow-xl hover:scale-[1.03] transition-all">
          <Sparkles className="w-5 h-5" /><span className="text-sm font-semibold">Ask AI</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed bottom-6 right-6 z-40 w-[min(26rem,calc(100vw-3rem))] h-[min(38rem,calc(100vh-3rem))] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center gap-2.5 px-4 py-3 bg-gradient-to-br from-blue-600 to-indigo-600 text-white">
            <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center"><Bot className="w-4 h-4" /></div>
            <div className="min-w-0">
              <p className="text-sm font-bold leading-tight">Dashboard Assistant</p>
              <p className="text-[11px] text-white/70 leading-tight">Answers from your LATAM data</p>
            </div>
            <div className="ml-auto flex items-center gap-0.5">
              <button onClick={() => setOpen(false)} title="Minimize (keep chat)" className="p-1.5 rounded-lg hover:bg-white/15"><Minus className="w-4 h-4" /></button>
              <button onClick={() => { setOpen(false); setMessages([]); setInput(''); }} title="Close & clear chat" className="p-1.5 rounded-lg hover:bg-white/15"><X className="w-4 h-4" /></button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.length === 0 ? (
              <div className="text-center mt-6">
                <div className="w-12 h-12 rounded-2xl bg-blue-100 text-blue-600 flex items-center justify-center mx-auto mb-3"><Sparkles className="w-6 h-6" /></div>
                <p className="text-sm font-semibold text-slate-700">Ask about your data</p>
                <p className="text-xs text-slate-400 mt-1 mb-4">Sales, plan, sell-out, inventory, digital — in English or Spanish.</p>
                <div className="space-y-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => ask(s)} className="block w-full text-left text-xs text-slate-600 bg-white border border-slate-200 rounded-lg px-3 py-2 hover:border-blue-300 hover:bg-blue-50/40 transition-colors">{s}</button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${m.role === 'user' ? 'bg-blue-600 text-white rounded-br-sm whitespace-pre-wrap' : 'bg-white border border-slate-200 text-slate-700 rounded-bl-sm'}`}>
                    {m.role === 'assistant'
                      ? (m.content
                          ? <RichText text={m.content} />
                          : (busy && i === messages.length - 1 ? <span className="inline-flex gap-1 items-center text-slate-400"><span className="w-1.5 h-1.5 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} /><span className="w-1.5 h-1.5 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: '120ms' }} /><span className="w-1.5 h-1.5 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: '240ms' }} /></span> : ''))
                      : m.content}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Input */}
          <div className="p-3 border-t border-slate-200 bg-white">
            <div className="flex items-end gap-2">
              <textarea ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKey} rows={1} placeholder="Ask about your data…"
                className="flex-1 resize-none max-h-24 text-sm border border-slate-300 rounded-xl px-3 py-2 focus:outline-none focus:border-blue-400" />
              <button onClick={() => ask()} disabled={busy || !input.trim()} className="p-2.5 rounded-xl bg-blue-600 text-white disabled:opacity-40 hover:bg-blue-700 transition-colors shrink-0"><Send className="w-4 h-4" /></button>
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5 text-center">AI can make mistakes — verify important figures.</p>
          </div>
        </div>
      )}
    </>
  );
};
